import React, { useEffect, useState } from 'react';
import { Check, Wallet, Sparkles } from 'lucide-react';
import { fetchPartnerQueue, approvePartner } from '../../lib/api';

const SanctuaryQueue = () => {
  const [queue, setQueue] = useState(null);
  const [busy, setBusy] = useState(null);
  const [lastApproved, setLastApproved] = useState(null);

  const load = () => {
    fetchPartnerQueue().then(setQueue).catch(() => setQueue({ count: 0, partners: [] }));
  };

  useEffect(() => { load(); }, []);

  const approve = async (partner) => {
    setBusy(partner.id);
    try {
      const res = await approvePartner(partner.id);
      setLastApproved({ partner, result: res });
      load();
    } finally {
      setBusy(null);
    }
  };

  if (!queue)
    return <div data-testid="queue-loading" className="h-40 bg-[#C1D7E2]/40 animate-pulse" />;

  const visibleQueue = queue.partners.filter((p) => !p.name.startsWith('TEST_'));

  return (
    <div data-testid="queue-view" className="space-y-10">
      <header className="border-b border-[#C1D7E2] pb-6 flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#90B0BF] mb-2">
            Sanctuary Onboarding
          </p>
          <h3 className="font-serif text-3xl md:text-4xl text-[#7C9CB1] font-light">
            Pending sanctuaries
          </h3>
          <p className="mt-2 font-serif italic text-[#90B0BF]">
            Approving a sanctuary triggers the automated Embedded Wallet + SBT mint.
          </p>
        </div>
        <p className="font-mono text-[11px] tracking-[0.25em] uppercase text-[#90B0BF]">
          {visibleQueue.length} pending
        </p>
      </header>

      {visibleQueue.length === 0 ? (
        <p data-testid="queue-empty" className="font-serif italic text-[#7C9CB1] py-8">
          The queue is quiet — every sanctuary is onboarded.
        </p>
      ) : (
        <ul className="divide-y divide-[#C1D7E2]">
          {visibleQueue.map((p, i) => (
            <li
              key={p.id}
              data-testid={`queue-item-${i}`}
              className="py-5 flex flex-wrap items-center justify-between gap-4"
            >
              <div className="min-w-0">
                <p className="font-serif text-xl text-[#7C9CB1]">{p.name}</p>
                <p className="font-mono text-[11px] text-[#90B0BF] mt-1">
                  {p.property_type} · {p.rooms} rooms · {p.location || '—'}
                </p>
                <p className="font-mono text-[11px] text-[#7C9CB1] mt-0.5">
                  {p.contact_name} · {p.contact_email}
                </p>
              </div>
              <button
                onClick={() => approve(p)}
                disabled={busy === p.id}
                data-testid={`queue-approve-${i}`}
                className="inline-flex items-center gap-2 bg-[#7C9CB1] text-[#F4F8F9] px-5 py-3 text-[11px] tracking-[0.22em] uppercase hover:bg-[#90B0BF] transition-colors disabled:opacity-60"
              >
                {busy === p.id ? 'Minting…' : (<><Check size={14} strokeWidth={1.5} /> Approve + Mint SBT</>)}
              </button>
            </li>
          ))}
        </ul>
      )}

      {lastApproved && (
        <div
          data-testid="queue-approved-card"
          className="border border-[#7C9CB1] bg-[#C1D7E2]/30 p-6 flex items-start gap-4"
        >
          <Sparkles size={24} strokeWidth={1.2} className="text-[#7C9CB1] mt-1" />
          <div className="min-w-0">
            <p className="font-serif text-xl text-[#7C9CB1]">
              {lastApproved.partner.name} approved.
            </p>
            <p className="mt-2 font-mono text-[11px] text-[#7C9CB1] break-all">
              SBT · {lastApproved.result.sbt_token_id}
              {'  '} · chain {lastApproved.result.chain_hash}
            </p>
            <p className="mt-1 font-mono text-[11px] text-[#90B0BF] flex items-center gap-2">
              <Wallet size={11} strokeWidth={1.5} />
              Metadata {lastApproved.result.metadata_uri}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default SanctuaryQueue;
